import { MouseEvent, useCallback, useMemo } from "react";
import { StatusCircleErrorXIcon } from "@fluentui/react-icons-mdl2";
import { Stack } from "@fluentui/react/lib/Stack";
import { Text } from "@fluentui/react/lib/Text";
import { TooltipHost, TooltipOverflowMode } from "@fluentui/react/lib/Tooltip";

import { getFormattedValue, getTranslatedFieldLabel, getTranslatedShortFieldLabel } from "Utils/Field";
import { EntityType } from "Model/EntityType";
import { WithTranslator } from "Components/Generic/Translator";
import { WithConfiguration } from "Components/Generic/Configuration";
import { IProject } from "Model/Storage/IProject";
import { containsOperator, isOperator, parseOperatorAndValue } from "Utils/Filter";
import { useChipStyles } from "./UseChipStyles";
import { IChipProps } from "./IChipProps";

export const Chip = WithTranslator(
  WithConfiguration((props: IChipProps): JSX.Element => {
    const {
      config, fieldName, onEdit, onRemove, search, translator
    } = props;
    const styles = useChipStyles();
    const meta = config.getMetadata(fieldName, EntityType.Project);
    const onEditClick = useCallback(() => onEdit(fieldName, search), [fieldName, onEdit, search]);

    const onRemoveClick = useCallback(
      (e: MouseEvent<HTMLSpanElement>) => {
        e.stopPropagation();
        onRemove(fieldName, search);
      },
      [fieldName, onRemove, search]
    );

    const [shortFieldLabel, longFieldLabel] = useMemo(() => {
      if (fieldName === `*`) {
        return ``;
      }

      return [
        getTranslatedShortFieldLabel(fieldName, EntityType.Project, translator),
        getTranslatedFieldLabel(fieldName, EntityType.Project, translator)
      ];
    }, [fieldName, translator]);

    let formattedValue = ``;
    if (containsOperator(search)) {
      const [operator, value] = parseOperatorAndValue(search);
      const operatorMap = {
        "=": `=`,
        "<": `<`,
        ">=": `≥`,
        ">": `>`,
        "<=": `≤`
      };
      formattedValue = `${operatorMap[operator]} ${getFormattedValue(
        { [fieldName]: value } as IProject,
        meta,
        translator,
        `-`
      )}`;
    } else if (isOperator(search)) {
      formattedValue = translator.get(`filter.operator.${search}`, search);
    } else {
      formattedValue = getFormattedValue({ [fieldName]: search } as IProject, meta, translator, `-`);
    }

    return (
      <Stack horizontal className={styles.root} onClick={onEditClick}>
        <Stack.Item disableShrink className={styles.field}>
          <Text className={styles.fieldText}>
            <TooltipHost content={longFieldLabel}>{shortFieldLabel}</TooltipHost>
          </Text>
        </Stack.Item>
        <Stack.Item disableShrink>
          <div className={styles.arrowRight} />
        </Stack.Item>
        <Stack.Item shrink={4} className={styles.search}>
          <Text className={styles.searchText}>
            <TooltipHost overflowMode={TooltipOverflowMode.Parent} content={search}>
              {formattedValue}
            </TooltipHost>
          </Text>
        </Stack.Item>
        <Stack.Item disableShrink>
          <StatusCircleErrorXIcon className={styles.icon} onClick={onRemoveClick} />
        </Stack.Item>
      </Stack>
    );
  })
);
